/**
 * FeedbackCard, the little "how was that?" card in the corner of the portal.
 *
 * Same idea as a PostHog survey popup, built in-house so it looks like
 * ours (fox, paper, Outfit) and so we control when it shows. One
 * question, five faces, an optional line of text once they've tapped a
 * face. Answers go to PostHog as events, so they sit next to the
 * onboarding funnel they're about.
 *
 * When it shows: a page that just finished something (onboarding for
 * now) drops a key in sessionStorage, `ml_feedback_prompt`. The card
 * reads it on the next authenticated page, waits a few seconds so it
 * doesn't land on top of the page they came for, then slides in.
 *
 * Once someone has actually answered we write profiles.feedback_given_at
 * and never ask again, on any device. A dismissal ("not now") is only
 * remembered in localStorage on that browser, since it isn't a forever
 * no.
 *
 * To add a second prompt (say, after a case closes): add a key to
 * PROMPTS in lib/feedback.ts and call requestFeedback('case_closed')
 * where it ends.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Angry, Frown, Meh, Smile, Laugh, Send } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { phFeedbackSubmitted, phFeedbackDismissed } from '../lib/posthog';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiConfig';
import {
  PROMPTS,
  SESSION_KEY,
  DEFAULT_PROMPT,
  DEFAULT_PROMPT_PATHS,
  doneKey,
  anyFeedbackDone,
  type FeedbackPromptKey,
} from '../lib/feedback';
import '@styles/components/feedback-card.css';

const SHOW_DELAY_MS = 6000;
/* The general prompt waits a little longer, so it never lands on top of
   the reason they came. */
const DEFAULT_DELAY_MS = 10000;

/** Has this person already sent an answer, on any device? Reads the
 *  column added in migration 102; if that isn't applied yet we just fall
 *  back to the per-browser memory rather than breaking the card. */
async function answeredBefore(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('feedback_given_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) return false;
  return Boolean((data as { feedback_given_at: string | null } | null)?.feedback_given_at);
}

function readRequestedPrompt(): FeedbackPromptKey | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw && raw in PROMPTS ? (raw as FeedbackPromptKey) : null;
  } catch {
    return null;
  }
}

const FACES: { score: 1 | 2 | 3 | 4 | 5; Icon: typeof Smile; label: string }[] = [
  { score: 1, Icon: Angry, label: 'Rough' },
  { score: 2, Icon: Frown, label: 'Not great' },
  { score: 3, Icon: Meh, label: 'Okay' },
  { score: 4, Icon: Smile, label: 'Good' },
  { score: 5, Icon: Laugh, label: 'Great' },
];

export default function FeedbackCard() {
  const { user } = useAuth();
  const location = useLocation();
  /* A page asked for something specific (onboarding just finished)? Use
     that. Otherwise the general question, but only on the home pages. */
  const [requested] = useState<FeedbackPromptKey | null>(readRequestedPrompt);
  const onHomePage = DEFAULT_PROMPT_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  const promptKey: FeedbackPromptKey | null = requested ?? (onHomePage ? DEFAULT_PROMPT : null);
  const [visible, setVisible] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);

  /* Wait a bit, then show, unless this user already answered or closed
     a card on an earlier visit. The general prompt also stays away if
     they've answered any prompt at all: one question per person. */
  useEffect(() => {
    if (!user || !promptKey || visible) return;
    const delay = requested ? SHOW_DELAY_MS : DEFAULT_DELAY_MS;
    let cancelled = false;
    const t = setTimeout(async () => {
      let alreadyDone = false;
      try {
        alreadyDone = requested
          ? !!localStorage.getItem(doneKey(user.id, promptKey))
          : anyFeedbackDone(user.id);
        if (alreadyDone && requested) sessionStorage.removeItem(SESSION_KEY);
      } catch {
        alreadyDone = true;
      }
      /* Nothing on this browser, but they may have answered on another one. */
      if (!alreadyDone) alreadyDone = await answeredBefore(user.id);
      if (!cancelled && !alreadyDone) setVisible(true);
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user, promptKey, requested, visible]);

  const finish = (how: 'answered' | 'dismissed') => {
    if (!user || !promptKey) return;
    try {
      localStorage.setItem(doneKey(user.id, promptKey), how);
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* nothing to do */
    }
  };

  const dismiss = () => {
    if (!promptKey) return;
    phFeedbackDismissed({ prompt: promptKey, score });
    finish('dismissed');
    setVisible(false);
  };

  const submit = () => {
    if (!user || !promptKey || score === null) return;
    const payload = { prompt: promptKey, score, comment: comment.trim() || null };
    phFeedbackSubmitted(payload);
    /* Remember it on the profile so we never ask this person again,
       whatever browser they're on. Fire and forget. */
    supabase
      .from('profiles')
      .update({ feedback_given_at: new Date().toISOString() })
      .eq('id', user.id)
      .then(undefined, () => {
        /* column missing or offline — localStorage still covers this browser */
      });

    /* Email to the team, fire and forget. The thank-you doesn't wait on it. */
    supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      if (!token) return;
      fetch(`${API_BASE}/notify/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      }).catch(() => {
        /* non-fatal */
      });
    });
    finish('answered');
    setDone(true);
    setTimeout(() => setVisible(false), 2400);
  };

  if (!promptKey || !visible) return null;
  const prompt = PROMPTS[promptKey];

  return (
    <div className="fbk" role="dialog" aria-label={prompt.title} aria-live="polite">
      <button type="button" className="fbk-close" onClick={dismiss} aria-label="Close">
        <X size={16} strokeWidth={2.25} />
      </button>

      {done ? (
        <div className="fbk-thanks">
          <img src="/images/mascot/fox-avatar.png" alt="" className="fbk-fox" />
          <div>
            <div className="fbk-title">Thank you</div>
            <div className="fbk-sub">That goes straight to the team.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="fbk-head">
            <img src="/images/mascot/fox-avatar.png" alt="" className="fbk-fox" />
            <div>
              <div className="fbk-title">{prompt.title}</div>
              <div className="fbk-sub">{prompt.sub}</div>
            </div>
          </div>

          <div className="fbk-faces" role="radiogroup" aria-label="Rating">
            {FACES.map(({ score: s, Icon, label }) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={score === s}
                aria-label={label}
                title={label}
                className={`fbk-face ${score === s ? 'is-on' : ''} ${score !== null && score !== s ? 'is-off' : ''}`}
                onClick={() => setScore(s)}
              >
                <Icon size={30} strokeWidth={1.6} />
              </button>
            ))}
          </div>
          <div className="fbk-scale" aria-hidden>
            <span>{FACES[0].label}</span>
            <span>{FACES[4].label}</span>
          </div>

          {score !== null && (
            <div className="fbk-more">
              <textarea
                className="fbk-input"
                placeholder={prompt.followUp}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                maxLength={500}
                autoFocus
              />
              <button type="button" className="fbk-send" onClick={submit}>
                <Send size={14} />
                Send
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
